import { ReactElement, ReactNode, useEffect, useRef, useState } from 'react';
import { DataTestId } from '@mews-commander/enums';
import { DefaultErrorBoundary, SuspenseBoundary, useConfirmationDialog } from '@mews-commander/ui';
import { FloatingPanel, FloatingPanelBaseProps } from '@mews/b2b-ui';
import { lt } from '@mews/framework';
import { Button, Container } from '@optimus-web/core';
import { PrimaryActionProps } from '../common';
import { CommanderFormProps, DialogFormRef } from './types';

export interface DialogFormProps extends Omit<FloatingPanelBaseProps, 'loading' | 'footer'> {
    id?: string;
    renderForm: (props: CommanderFormProps) => ReactElement<CommanderFormProps>;
    renderPrimaryAction?: (props: PrimaryActionProps) => ReactElement<PrimaryActionProps>;
    onClose?: () => void;
    onSuccess?: () => void;
    saveLabel?: ReactNode;
    submitButtonDataTestId?: DataTestId;
    cancelLabel?: ReactNode;
    suspenseLoader?: 'inline' | 'overlay';
    'data-test-id'?: string;
}

export const DialogForm = ({
    id,
    renderForm,
    renderPrimaryAction,
    onClose,
    onSuccess,
    saveLabel = lt('Save'),
    cancelLabel = lt('Cancel'),
    suspenseLoader,
    submitButtonDataTestId,
    'data-test-id': dataTestId,
    ...props
}: DialogFormProps) => {
    const formRef = useRef<DialogFormRef | null>(null);
    const [loading, setLoading] = useState(false);

    const mounted = useRef(false);
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);
    const handleCallback = (callback: () => void) => () => {
        if (mounted.current) {
            callback();
        }
    };

    const handleClose = () => {
        if (formRef.current?.hasDirtyFields) {
            openConfirmationDialog();
        } else {
            onClose?.();
        }
    };

    const handleSubmit = () => {
        void formRef.current?.submit();
    };

    const { confirmationDialog, openConfirmationDialog } = useConfirmationDialog({
        onConfirm: () => onClose?.(),
    });
    return (
        <>
            <FloatingPanel
                {...props}
                onClose={handleClose}
                loading={loading}
                footer={{
                    primary: renderPrimaryAction ? (
                        renderPrimaryAction({
                            id,
                            handleSubmit: (e) => {
                                e.preventDefault();
                                handleSubmit();
                            },
                            disabled: loading,
                        })
                    ) : (
                        <Button
                            type="submit"
                            form={id}
                            variant="primary"
                            onClick={handleSubmit}
                            disabled={loading}
                            data-test-id={submitButtonDataTestId}
                        >
                            {saveLabel}
                        </Button>
                    ),
                    secondary: (
                        <Button onClick={handleClose} type="submit">
                            {cancelLabel}
                        </Button>
                    ),
                }}
                data-test-id={dataTestId ?? id}
            >
                <DefaultErrorBoundary>
                    <SuspenseBoundary loader={suspenseLoader}>
                        <Container padding={['0', '0', '300', '0']}>
                            {renderForm({
                                id,
                                formRef,
                                onSubmitting: handleCallback(() => setLoading(true)),
                                onSubmitted: handleCallback(() => setLoading(false)),
                                onSuccess: handleCallback(() => {
                                    onSuccess?.();
                                    onClose?.();
                                }),
                            })}
                        </Container>
                    </SuspenseBoundary>
                </DefaultErrorBoundary>
            </FloatingPanel>
            {confirmationDialog}
        </>
    );
};
